import React, { useEffect, useState } from 'react';
import { fetchReviewsForTarget, calculateReviewStats } from '../../services/reviewService';
import ReviewCard from './ReviewCard';
import { Star } from 'lucide-react';

const ReviewDisplay = ({ targetType, targetId }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, total: 0, distribution: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReviews = async () => {
      setLoading(true);
      const data = await fetchReviewsForTarget(targetId);
      setReviews(data);
      setStats(calculateReviewStats(data));
      setLoading(false);
    };

    if (targetId) {
      loadReviews();
    }
  }, [targetId]);

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500 dark:text-gray-400 font-medium">
        لا توجد تقييمات حتى الآن. كن أول من يشاركنا رأيه!
      </div>
    );
  }

  return (
    <div className="space-y-12 rtl" dir="rtl">

      {/* Overall Rating Header */}
      <div className="bg-white dark:bg-[#0B0F19] dark:bg-opacity-40 p-8 rounded-2xl border border-gray-100 dark:border-white/10 shadow-sm flex flex-col md:flex-row items-center gap-8 md:gap-16">

        {/* Massive Average Score */}
        <div className="flex flex-col items-center justify-center w-40 flex-shrink-0">
          <div className="text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-amber-200 to-amber-500 mb-2">
            {stats.average}
          </div>
          <div className="flex items-center space-x-1 rtl:space-x-reverse mb-2">
            {[1, 2, 3, 4, 5].map(star => (
              <Star
                key={star}
                size={20}
                className={star <= Math.round(stats.average) ? "fill-amber-400 text-amber-400" : "fill-transparent text-gray-300 dark:text-gray-600"}
              />
            ))}
          </div>
          <p className="text-md font-medium text-gray-500 dark:text-gray-400">
            {stats.total} تقييم
          </p>
        </div>

        {/* Breakdown Bar Chart */}
        <div className="flex-1 w-full space-y-3">
          {[5, 4, 3, 2, 1].map(stars => {
            const count = stats.distribution[stars] || 0;
            const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;

            return (
              <div key={stars} className="flex items-center gap-4">
                <span className="w-16 font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  {stars} <Star size={14} className="fill-amber-400 text-amber-400" />
                </span>
                <div className="flex-1 h-3 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="w-12 text-md font-medium text-gray-500 dark:text-gray-400 text-left">
                  {percentage}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review Cards Carousel */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white tracking-wide mb-6">
          آراء الطلاب المتدربين
        </h3>
        {/* Horizontal Carousel Container */}
        <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pt-4 px-2 -mx-2 hide-scrollbar">
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {/* Helper style to hide scrollbar but keep functionality */}
        <style dangerouslySetInnerHTML={{
          __html: `
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}} />
      </div>

    </div>
  );
};

export default ReviewDisplay;
